import { SpinnerScreen } from '@workspace/ui/components/spinner';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

export interface TokenPageProps {
    /** Message affiché pendant la validation (défaut: "Authentification en cours...") */
    loadingText?: string;
    /** Hook useValidateToken configuré pour l'app */
    useValidateTokenHook: () => {
        mutate: (token: string, options?: { onError?: () => void }) => void;
    };
}

/**
 * Page de validation de token SSO (auto-login depuis espace conseiller)
 *
 * Récupère le `access_token` depuis les query params,
 * le valide via l'API backend et redirige vers l'app.
 *
 * @example
 * ```tsx
 * import { TokenPage } from '@workspace/auth';
 * import { useValidateToken } from '@/hooks/auth';
 *
 * export default function DealsTokenPage() {
 *   return <TokenPage useValidateTokenHook={useValidateToken} />;
 * }
 * ```
 */
export function TokenPage({
    loadingText = 'Authentification en cours...',
    useValidateTokenHook,
}: TokenPageProps) {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const validateMutation = useValidateTokenHook();
    const [hasAttempted, setHasAttempted] = useState(false);

    useEffect(() => {
        if (hasAttempted) return;

        const accessToken = searchParams.get('access_token');

        if (!accessToken) {
            navigate('/auth/login');
            return;
        }

        setHasAttempted(true);
        validateMutation.mutate(accessToken, {
            onError: () => {
                navigate('/auth/login');
            },
        });
    }, [searchParams, navigate, hasAttempted, validateMutation]);

    return <SpinnerScreen text={loadingText} />;
}
